/**
 * EditActivityModal — focused field-edit form for an existing activity.
 *
 * Wraps the same `ActivityFields` component the wizard's DetailsStep
 * uses, so every field (quantity, period, region, fuel/vehicle/refrigerant/
 * process type, name, notes, fallbacks) renders identically. `scope` and
 * `activityType` are locked — changing those amounts to creating a
 * different activity, which the user should do via the wizard.
 *
 * On Save, the modal computes a minimal patch of changed fields, sends
 * it through `useUpdateActivityMutation`, and closes on success. The
 * existing calculation-history query invalidation downstream picks up
 * the recalculated value automatically.
 */

import { useEffect, useMemo, useState } from 'react';
import { Spinner } from 'reactstrap';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@g17eco/ui-modern/components/ui/dialog';
import { Button } from '@g17eco/ui-modern/components/ui/button';
import { useUpdateActivityMutation } from '../api/emissions';
import {
  type ActivityRecord,
  type ActivityType,
  type ActivityUnit,
  type FuelType,
  type FugitiveGasType,
  type ProcessType,
  type RefrigerantGasType,
  type UpdateActivityRequest,
  type VehicleType,
  EmissionScope,
} from '../types/emissions';
import { ActivityFields, type ActivityFieldValues } from './form/ActivityFields';
import { expandMonthToPeriodRange } from '../utils/activityUtils';

export interface EditActivityModalProps {
  isOpen: boolean;
  onClose: () => void;
  initiativeId: string;
  activity: ActivityRecord;
}

const LOCKED_FIELDS = new Set(['scope', 'activityType']);

/**
 * Project an `ActivityRecord` into the value shape `ActivityFields`
 * expects (its strings-and-blanks form), so unfilled optional fields
 * round-trip correctly without becoming literal `undefined` strings.
 */
const recordToFieldValues = (activity: ActivityRecord): ActivityFieldValues => ({
  scope: activity.scope,
  activityType: activity.activityType as ActivityType,
  // Period fields come back from the API as full ISO timestamps; the
  // wizard's MonthPicker expects "YYYY-MM" so we trim accordingly.
  periodStart: activity.periodStart ? activity.periodStart.slice(0, 7) : '',
  periodEnd: activity.periodEnd ? activity.periodEnd.slice(0, 7) : '',
  quantity: activity.quantity != null ? String(activity.quantity) : '',
  unit: (activity.unit ?? '') as ActivityUnit | '',
  fuelType: (activity.fuelType ?? '') as FuelType | '',
  vehicleType: (activity.vehicleType ?? '') as VehicleType | '',
  refrigerantType: (activity.refrigerantType ?? '') as RefrigerantGasType | FugitiveGasType | '',
  processType: (activity.processType ?? '') as ProcessType | '',
  region: activity.region ?? '',
  regionFallback: activity.regionFallback ?? false,
  yearFallback: activity.yearFallback ?? false,
  name: activity.name ?? '',
  dataSource: activity.dataSource ?? '',
  notes: activity.notes ?? '',
});

/**
 * Build a minimal `UpdateActivityRequest` patch from the diff between
 * the original activity and the current form values. Only fields that
 * actually changed are sent — the API treats undefined as "leave alone".
 */
const buildPatch = (
  original: ActivityRecord,
  values: ActivityFieldValues,
): UpdateActivityRequest => {
  const patch: UpdateActivityRequest = {};

  // Period: if either side changed, send both expanded to ISO ranges.
  const originalStart = original.periodStart?.slice(0, 7) ?? '';
  const originalEnd = original.periodEnd?.slice(0, 7) ?? '';
  if (values.periodStart !== originalStart || values.periodEnd !== originalEnd) {
    const { periodStart, periodEnd } = expandMonthToPeriodRange(values.periodStart, values.periodEnd);
    patch.periodStart = periodStart;
    patch.periodEnd = periodEnd;
  }

  if (values.quantity !== String(original.quantity ?? '')) {
    const n = Number(values.quantity);
    if (Number.isFinite(n)) patch.quantity = n;
  }
  if (values.unit && values.unit !== original.unit) {
    patch.unit = values.unit as ActivityUnit;
  }
  if ((values.fuelType || undefined) !== original.fuelType) {
    patch.fuelType = (values.fuelType || undefined) as FuelType | undefined;
  }
  if ((values.vehicleType || undefined) !== original.vehicleType) {
    patch.vehicleType = (values.vehicleType || undefined) as VehicleType | undefined;
  }
  if ((values.refrigerantType || undefined) !== (original.refrigerantType ?? original.gasType)) {
    // The form bundles refrigerant + fugitive gas under one field; we
    // can't know which slot to write without the activity type. Send to
    // refrigerantType — backend accepts either; it'll route correctly.
    patch.refrigerantType = (values.refrigerantType || undefined) as RefrigerantGasType | undefined;
  }
  if ((values.processType || undefined) !== original.processType) {
    patch.processType = (values.processType || undefined) as ProcessType | undefined;
  }
  if (values.region && values.region !== original.region) {
    patch.region = values.region;
  }
  if (values.regionFallback !== (original.regionFallback ?? false)) {
    patch.regionFallback = values.regionFallback;
  }
  if (values.yearFallback !== (original.yearFallback ?? false)) {
    patch.yearFallback = values.yearFallback;
  }
  if (values.name !== (original.name ?? '')) {
    patch.name = values.name || undefined;
  }
  if (values.dataSource !== (original.dataSource ?? '')) {
    patch.dataSource = values.dataSource || undefined;
  }
  if (values.notes !== (original.notes ?? '')) {
    patch.notes = values.notes || undefined;
  }
  return patch;
};

const isPatchEmpty = (patch: UpdateActivityRequest): boolean =>
  Object.keys(patch).length === 0;

export const EditActivityModal = ({ isOpen, onClose, initiativeId, activity }: EditActivityModalProps) => {
  const [values, setValues] = useState<ActivityFieldValues>(() => recordToFieldValues(activity));
  const [error, setError] = useState<string | null>(null);

  const [updateActivity, { isLoading: isSaving }] = useUpdateActivityMutation();

  // Re-seed when the modal opens or the underlying activity changes
  // (e.g. user opens the modal, edits, cancels, then reopens — we want
  // the latest persisted values, not the abandoned draft).
  useEffect(() => {
    if (isOpen) {
      setValues(recordToFieldValues(activity));
      setError(null);
    }
  }, [isOpen, activity]);

  const handleUpdate = (updates: Partial<ActivityFieldValues>) => {
    setValues((prev) => ({ ...prev, ...updates }));
  };

  const patch = useMemo(() => buildPatch(activity, values), [activity, values]);
  const hasChanges = !isPatchEmpty(patch);

  // The form requires (at minimum) period and quantity to be set. We
  // don't reach into ActivityFields' validation; we just block save on
  // the obvious empty cases.
  const canSave =
    hasChanges &&
    !isSaving &&
    values.periodStart !== '' &&
    values.periodEnd !== '' &&
    values.quantity !== '' &&
    Number(values.quantity) > 0 &&
    values.unit !== '';

  const handleSave = async () => {
    setError(null);
    if (!canSave) return;
    const result = await updateActivity({
      initiativeId,
      activityId: activity._id,
      data: patch,
    });
    if ('error' in result) {
      setError('Failed to save changes. Check the highlighted fields and try again.');
      return;
    }
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      {/* sm:max-w-5xl ≈ 1024px — wide enough for the wizard's 5-column
          fuel/vehicle/refrigerant tile grid without squeezing the
          two-column "Additional Details" row underneath. */}
      <DialogContent className='tw:sm:max-w-5xl tw:max-h-[90vh] tw:overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>Edit activity</DialogTitle>
          <DialogDescription>
            Scope and Activity Type are fixed — to record a different activity type, create a new activity.
          </DialogDescription>
        </DialogHeader>

        <ActivityFields
          values={values}
          onUpdate={handleUpdate}
          lockedFields={LOCKED_FIELDS}
          defaultDetailsOpen
        />

        {/* Wizard's ActivityFields uses an empty-string `scope` placeholder
            during initial onboarding; our edit context always has a real
            scope, but the type union still admits ''. Defensive guard. */}
        {values.scope === ('' as EmissionScope | '') && (
          <p className='tw:text-xs tw:text-destructive tw:mb-0'>Activity is missing scope; cannot edit.</p>
        )}

        <DialogFooter>
          {error && (
            <p className='tw:text-xs tw:text-destructive tw:mb-0 tw:mr-auto tw:self-center'>{error}</p>
          )}
          <Button variant='outline' size='sm' onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button size='sm' onClick={handleSave} disabled={!canSave}>
            {isSaving ? (
              <>
                <Spinner size='sm' className='tw:me-1' />
                Saving…
              </>
            ) : (
              'Save changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
